/**
 * Selects the items in a received-items batch that have not been applied to this save yet.
 *
 * Archipelago resends every item ever received whenever a client reconnects, and batches can overlap
 * arbitrarily with what was already applied. Every item has an absolute index (its position in the
 * slot's full received-items list), so the client stores how many it has applied and skips anything
 * below that.
 *
 * This module deliberately has no imports so it stays trivially testable.
 *
 * @param items The batch of items received.
 * @param startingIndex The absolute index of items[0].
 * @param appliedCount How many items this save has already applied, i.e. the next index to apply.
 * @returns The items to apply, each with its absolute index, in order.
 */
export function selectUnappliedItems<T>(
  items: readonly T[],
  startingIndex: number,
  appliedCount: number,
): { item: T; index: number }[] {
  const unapplied: { item: T; index: number }[] = [];

  for (let i = 0; i < items.length; i++) {
    const index = startingIndex + i;
    if (index < appliedCount) {
      continue;
    }
    unapplied.push({ item: items[i], index });
  }

  return unapplied;
}
